import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useTranslation } from "react-i18next";

export type ContentTypeValue = "document" | "video";

export type ContentTypeSegmentedControlProps = {
  value: ContentTypeValue;
  onChange: (value: ContentTypeValue) => void;
  /** When set, shows (n) after the Documents label. */
  documentCount?: number;
  /** When set, shows (n) after the Videos label. */
  videoCount?: number;
  disabled?: boolean;
  "aria-label"?: string;
};

/**
 * Shared Documents / Videos segmented control for Document Management
 * (Upload panel and library list). Fixed height + equal segment widths
 * to avoid layout shift when switching.
 */
export function ContentTypeSegmentedControl({
  value,
  onChange,
  documentCount,
  videoCount,
  disabled = false,
  "aria-label": ariaLabel,
}: ContentTypeSegmentedControlProps) {
  const { t } = useTranslation();
  const documentsLabel = t("opva.dmUploadTabDocuments", {
    defaultValue: "Documents",
  });
  const videosLabel = t("opva.dmUploadTabVideos", { defaultValue: "Videos" });

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      disabled={disabled}
      onChange={(_, next: ContentTypeValue | null) => {
        if (next != null) onChange(next);
      }}
      aria-label={
        ariaLabel ??
        t("opva.dmContentTypeSwitchAria", {
          defaultValue: "Content type",
        })
      }
      sx={{
        p: 0.5,
        bgcolor: "#F5F7FA",
        borderRadius: "10px",
        height: 40,
        minHeight: 40,
        maxHeight: 40,
        minWidth: { xs: 268, sm: 300 },
        boxSizing: "border-box",
        flexShrink: 0,
        display: "inline-grid",
        gridTemplateColumns: "1fr 1fr",
        alignItems: "stretch",
        width: { xs: "100%", sm: "auto" },
        maxWidth: "100%",
        "& .MuiToggleButtonGroup-grouped": {
          border: 0,
          mx: 0,
          px: { xs: 1.5, sm: 2 },
          minWidth: 0,
          width: "100%",
          height: 32,
          boxSizing: "border-box",
          borderRadius: "8px !important",
          textTransform: "none",
          // Keep weight constant so selection does not resize segments.
          fontWeight: 600,
          fontSize: "0.875rem",
          lineHeight: 1.2,
          color: "text.secondary",
          cursor: "pointer",
          gap: 0.5,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          whiteSpace: "nowrap",
          transition:
            "background-color 180ms ease, color 180ms ease, box-shadow 180ms ease",
          "&.Mui-selected": {
            bgcolor: "primary.main",
            color: "common.white",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "primary.dark",
            },
          },
          "&:not(.Mui-selected):hover": {
            bgcolor: "action.hover",
          },
          "&.Mui-disabled": {
            cursor: "not-allowed",
          },
        },
      }}
    >
      <ToggleButton value="document" aria-label={documentsLabel}>
        <DescriptionOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
        {documentsLabel}
        {documentCount != null ? (
          <SegmentCount count={documentCount} selected={value === "document"} />
        ) : null}
      </ToggleButton>
      <ToggleButton value="video" aria-label={videosLabel}>
        <VideocamOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
        {videosLabel}
        {videoCount != null ? (
          <SegmentCount count={videoCount} selected={value === "video"} />
        ) : null}
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

function SegmentCount({
  count,
  selected,
}: {
  count: number;
  selected: boolean;
}) {
  return (
    <span
      style={{
        opacity: selected ? 0.95 : 0.75,
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      ({count})
    </span>
  );
}
